import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";
import {
  getUploadedFile,
  getUploadedFileMetadata,
  IMediaUpload,
  mediaSelector,
  setOpenedSlider,
  setUploadedFile,
  uploadMultipart,
  setUploadNewTagsInSuggestions,
  getCurrentTagsOrganizationId,
  setOriginalMediaGallery,
  setUploadedOldFile,
  getUploadedOldFile,
  askForPublicationOf3DEnvironment,
} from "../state/gallerySlice";
import { fromFileReturnMD5AndSize } from "../state/uploadUtil";
import { IMedia } from "../../../model/unityObject";
import { MediaNameInput } from "../../../common/components/Input/MediaNameInput";
import { currentUserSelector } from "../../../common/state/selectors/authSelector";
import Spinner from "../../../common/components/Loader/Spinner";
import { fromFileReturnMD5AndSizeWorker } from "../state/uploadUtilWorkers";
import { useNavigate } from "react-router-dom";
import SlideOver, {
  classSlideCloseAnimation,
  sliderAnimationDurationInMs,
} from "../../../common/components/SlideOver/SlideOver";
import { closeSlider, setAnimationClassState } from "../../../common/state/slice/modal/modalSlice";
import Media360TagDropdown from "./Media360TagDropdown";
import { Dropdown } from "src/common/components/Input";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { InformationCircleIcon } from "@heroicons/react/outline";
import { militaryGradeClientGateAgainstSuspiciousOrSeditiousFiles } from "src/features/gallery/utils/securityGate";
import type { GateResult } from "src/features/gallery/utils/securityGateCore";
import { useAppDispatch } from "src/common/state/hooks";
import InformationFrame, {
  type InfoFrameStyle,
} from "src/common/components/InfomationFrame/InformationFrame";

const isGateFail = (g: GateResult): g is Extract<GateResult, { ok: false }> => g.ok === false;

export const UPLOAD_SLIDEOVER_COMPONENT_ID = "uploadSlideoverCompId";

export const UploadPanel = ({
  mobile = false,
  uploadFromSelectionCb = () => null,
}: {
  mobile?: boolean;
  uploadFromSelectionCb?: any;
}) => {
  const { handleSubmit } = useForm<IMediaUpload>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const tagsOrganization = useSelector(getCurrentTagsOrganizationId);
  const medias = useSelector(mediaSelector);
  const selectedFile = useSelector(getUploadedFile);
  const oldFile = useSelector(getUploadedOldFile);
  const selectedFileMetadata = useSelector(getUploadedFileMetadata);
  const currentUser = useSelector(currentUserSelector);

  const defaultMedia360TagIndex = 1;

  const [preview, setPreviewState] = useState("");
  const previewRef = useRef<string>("");

  const setPreview = (next: string) => {
    previewRef.current = next;
    setPreviewState(next);
  };

  const [MD5, setMD5] = useState("");
  const [media360Tag, setMedia360Tag] = useState("other");
  const [canUpload, setCanUpload] = useState(false);
  const [mediaAlreadyExists, setMediaAlreadyExists] = useState(false);
  const [isMd5Loaded, setIsMd5Loaded] = useState(false);
  const [mediaNameToEdit, setMediaNameToEdit] = useState<string>();
  const [media3DTypeOptions] = useState([
    { value: "others", optionText: t("pages.gallery.filters.objects3d") },
    { value: "media360", optionText: t("pages.gallery.filters.environments3d") },
  ]);
  const [media3DType, setMedia3DType] = useState("others");

  const [gateOk, setGateOk] = useState(false);
  const [md5Unique, setMd5Unique] = useState(false);
  const [nameUnique, setNameUnique] = useState(true);

  const [infoVisible, setInfoVisible] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [infoStyle, setInfoStyle] = useState<InfoFrameStyle>("warning");

  const gateRunIdRef = useRef(0);

  useEffect(() => {
    const isNameValid =
      mediaNameToEdit && mediaNameToEdit.length <= 55 && mediaNameToEdit.trim().length > 0;
    setCanUpload(gateOk && md5Unique && isNameValid);
  }, [gateOk, md5Unique, mediaNameToEdit]);

  const resetUploadState = () => {
    setInfoVisible(false);
    setInfoMessage("");
    setGateOk(false);
    setMd5Unique(false);
    setCanUpload(false);
    setMediaAlreadyExists(false);
    setMD5("");
    setNameUnique(true);
    setMediaNameToEdit(undefined);
    setPreview("");
    dispatch(setUploadedFile(undefined));
    dispatch(setUploadedOldFile(undefined));
  };

  const closeSliderAnimation = (isUpload = false) => {
    setTimeout(() => {
      if (isUpload) {
        dispatch(closeSlider(UPLOAD_SLIDEOVER_COMPONENT_ID));
      } else {
        dispatch(setOpenedSlider(""));
        dispatch(setUploadedFile(undefined));
        dispatch(setUploadedOldFile(undefined));
      }
      setInfoVisible(false);
    }, sliderAnimationDurationInMs);
    setTimeout(() => {
      dispatch(setAnimationClassState(classSlideCloseAnimation));
    }, 1);
  };

  useEffect(() => {
    setMediaNameToEdit(selectedFile?.name?.slice(0, selectedFile?.name?.lastIndexOf(".")));
    if (selectedFileMetadata?.needAskMedia360Tags && media360Tag === "other") {
      setMedia360Tag(selectedFileMetadata.media360Tags[defaultMedia360TagIndex]);
    }
  }, [selectedFileMetadata, media360Tag]);

  useEffect(() => {
    let canceled = false;
    const runId = ++gateRunIdRef.current;

    if (!selectedFile) {
      setPreview("");
      setGateOk(false);
      setMd5Unique(false);
      setCanUpload(false);
      setInfoVisible(false);
      return () => undefined;
    }

    const toObjectURL = (f: unknown): string | null => {
      try {
        if (f instanceof Blob) return URL.createObjectURL(f);
        return null;
      } catch (e) {
        console.error("toObjectURL failed:", e);
        return null;
      }
    };

    const initialUrl = toObjectURL(selectedFile);
    if (!initialUrl) {
      setInfoMessage(t("pages.gallery.uploadPanel.validationFailed") || "File failed validation.");
      setInfoStyle("danger");
      setInfoVisible(true);
      setGateOk(false);
      setMd5Unique(false);
      setCanUpload(false);
      return () => undefined;
    }

    setPreview(initialUrl);
    setGateOk(false);
    setMd5Unique(false);
    setCanUpload(false);

    const runSecurityGate = async () => {
      try {
        const gate = await militaryGradeClientGateAgainstSuspiciousOrSeditiousFiles(
          selectedFile as File,
          {
            expectedKind: selectedFileMetadata?.sourceMediaType as any,
            maxMegapixels: 100,
            maxBytes: 2_000_000_000,
            timeoutMs: 6000,
            reencodeImage: true,
          },
        );

        if (canceled || gateRunIdRef.current !== runId) return;

        if (isGateFail(gate)) {
          setPreview("");
          setGateOk(false);
          setCanUpload(false);
          setMediaAlreadyExists(false);
          setInfoMessage(t("pages.gallery.uploadPanel.problemWithThisFile"));
          setInfoStyle("danger");
          setInfoVisible(true);
          setIsMd5Loaded(true);
        } else {
          setGateOk(true);
          setInfoVisible(false);
          setMediaNameToEdit((prev) => prev ?? gate.sanitizedName);

          if (gate.previewObjectUrl) {
            try {
              let nextUrl = gate.previewObjectUrl;
              if (typeof nextUrl === "string" && nextUrl.startsWith("blob:")) {
                const resp = await fetch(nextUrl);
                const blob = await resp.blob();
                nextUrl = URL.createObjectURL(blob);
              }
              setPreview(nextUrl);
            } catch (e) {
              console.error("stabilize preview url failed:", e);
              setPreview(previewRef.current || "");
            }
          }
        }
      } catch (e) {
        if (canceled || gateRunIdRef.current !== runId) return;
        console.error("runSecurityGate failed:", e);
        setGateOk(false);
        setCanUpload(false);
        setInfoMessage("Validation failed unexpectedly.");
        setInfoStyle("danger");
        setInfoVisible(true);
        setIsMd5Loaded(true);
      }
    };

    runSecurityGate();

    const onMd5Return = (res: any) => {
      if (canceled || gateRunIdRef.current !== runId) return;
      const existsAlready = medias.find((m: IMedia) => m.MD5 === res.MD5);
      if (existsAlready !== undefined) {
        setMediaAlreadyExists(true);
        setMd5Unique(false);
      } else if (
        selectedFile &&
        oldFile &&
        selectedFile.name === oldFile.name &&
        selectedFile.size === oldFile.size &&
        selectedFile.lastModified === oldFile.lastModified
      ) {
        setMediaAlreadyExists(true);
        setMd5Unique(false);
      } else {
        setMediaAlreadyExists(false);
        setMD5(res.MD5);
        setMd5Unique(true);
      }
      setIsMd5Loaded(true);
    };

    setIsMd5Loaded(false);
    if (window.Worker) {
      try {
        fromFileReturnMD5AndSizeWorker(selectedFile as File, onMd5Return);
      } catch (e) {
        console.error("fromFileReturnMD5AndSizeWorker failed:", e);
        fromFileReturnMD5AndSize(selectedFile as File)
          .then(onMd5Return)
          .catch((err) => {
            console.error("fromFileReturnMD5AndSize fallback failed:", err);
            setIsMd5Loaded(true);
          });
      }
    } else {
      fromFileReturnMD5AndSize(selectedFile as File)
        .then(onMd5Return)
        .catch((e) => {
          console.error("fromFileReturnMD5AndSize failed:", e);
          setIsMd5Loaded(true);
        });
    }

    return () => {
      canceled = true;
    };
  }, [selectedFile, medias, oldFile, selectedFileMetadata, t]);

  const onSubmit = () => {
    const nameNotAvailable = medias.find(
      (m: IMedia) => m.name === (mediaNameToEdit ? mediaNameToEdit : (selectedFile as File)?.name),
    );

    if (nameNotAvailable) {
      setNameUnique(false);
      return;
    }

    if (!gateOk || !md5Unique) {
      dispatch(
        setAlert({
          type: "error",
          msg: t("pages.gallery.uploadPanel.validationFailed") || "File failed validation.",
        }),
      );
      setInfoMessage(t("pages.gallery.uploadPanel.validationFailed") || "File failed validation.");
      setInfoStyle("danger");
      setInfoVisible(true);
      return;
    }

    if (selectedFile && currentUser) {
      closeSliderAnimation(true);
      setCanUpload(false);

      dispatch(
        setUploadedOldFile({
          file: selectedFile as File,
          metadata: selectedFileMetadata,
        }),
      );

      if (selectedFileMetadata?.sourceMediaType === "model3d" && media3DType === "media360") {
        dispatch(
          askForPublicationOf3DEnvironment({
            file: selectedFile as File,
            name: mediaNameToEdit ? mediaNameToEdit : (selectedFile as File)?.name,
          }),
        )
          .then((res) => {
            if (res?.status === 200 && res?.data?.isSended) {
              dispatch(
                setAlert({
                  msg: t("general.emailToAskForPublicationWasSentSuccessfully"),
                  type: "success",
                }),
              );
            } else {
              dispatch(setAlert({ msg: t("error.genericServerError"), type: "error" }));
            }
          })
          .catch((e) => {
            console.error("askForPublicationOf3DEnvironment failed:", e);
            dispatch(setAlert({ msg: t("error.genericServerError"), type: "error" }));
          });
      } else {
        dispatch(
          uploadMultipart({
            file: selectedFile as File,
            orgId: currentUser.organization.id,
            name: mediaNameToEdit ? mediaNameToEdit : (selectedFile as File)?.name,
            media_360_tag: media360Tag,
            MD5,
            cb: (x: any) => {
              if (uploadFromSelectionCb) {
                uploadFromSelectionCb(x);
              }
              setTimeout(() => {
                dispatch(
                  setUploadNewTagsInSuggestions({ newTags: x.tags, oldTags: tagsOrganization }),
                );
                dispatch(setOriginalMediaGallery(x.id));
              }, 0);
            },
          }),
        );
      }
      if (mobile) {
        setInfoVisible(false);
        navigate("/uploads");
      }
    }
  };

  const ChooseAnotherFileButton = ({ className = "" }: { className?: string }) => (
    <button
      className={`btn-alternative-outline mr-3 mt-2 ${className}`}
      onClick={(e) => {
        e.preventDefault();
        resetUploadState();
      }}
    >
      {t("pages.gallery.uploadPanel.chooseAnotherFile")}
    </button>
  );

  return mobile ? (
    <div className="sticky flex flex-col top-0 right-0 pb-3 w-screenshadow-md over overflow-y-auto max-h-screen h-full">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
        <div className={"grow w-full bg-color-red"}>
          {selectedFile && selectedFileMetadata && !!preview && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <MediaPreview url={preview} type={selectedFileMetadata.sourceMediaType} mobile />
            </div>
          )}
          {!mediaAlreadyExists ? (
            <div className="ml-2">
              <div className="block font-medium text-gray-700 text-sm">
                {t("general.renameFile")}
              </div>
              <MediaNameInput
                className={`${mobile && "max-w-[65%]"}`}
                value={mediaNameToEdit}
                onChange={setMediaNameToEdit}
                maxLength={55}
              />
              {!nameUnique && <p className="text-red-600 ">NAME UNAVAILABLE</p>}

              {selectedFileMetadata?.needAskMedia360Tags && (
                <Media360TagDropdown
                  mediaType={String(selectedFileMetadata?.sourceMediaType)}
                  onChangeCb={(v: any) => {
                    setMedia360Tag(v);
                  }}
                  mode="upload"
                  mobile
                />
              )}
              {selectedFileMetadata?.sourceMediaType === "model3d" && (
                <div className="flex">
                  <Dropdown
                    options={media3DTypeOptions}
                    label={t("pages.gallery.filters.mediatype")}
                    onChangeCb={(e: any) => {
                      setMedia3DType(e);
                    }}
                    asDefaultValue
                    defaultValueIndex={0}
                  />
                  <div className="flex justify-end items-end ml-3 mb-2">
                    <Tooltip
                      message={t("pages.gallery.uploadPanel.tooltipfor3dTypes")}
                      forceSingleLine={false}
                      lineLength={"w-48"}
                    >
                      <InformationCircleIcon className="stroke-green-500 h-8 w-8 m-auto" />
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p>{t("pages.gallery.uploadPanel.fileAlreadyInGallery")}</p>
          )}
        </div>
        {isMd5Loaded ? (
          <div className={`${mobile && "h-full ml-2 flex items-center justify-center"}`}>
            <button
              className="btn-alternative-outline mr-3 mt-2"
              onClick={(e) => {
                e.preventDefault();
                dispatch(setUploadedFile(undefined));
                dispatch(setUploadedOldFile(undefined));
                setInfoVisible(false);
                navigate("/");
              }}
            >
              {t("general.cancel")}
            </button>
            {infoVisible ? (
              <ChooseAnotherFileButton />
            ) : (
              <button
                className="btn-primary-fill mr-3 mt-2"
                disabled={!canUpload || (selectedFileMetadata?.needAskMedia360Tags && !media360Tag)}
              >
                {t("pages.project.importMedia")}
              </button>
            )}
          </div>
        ) : (
          <div className={`flex flex-col mt-4 ${mobile && "ml-2"}`}>
            <Spinner relative />
            <p className="text-center italic">{t("pages.gallery.uploadPanel.preparingMedia")}</p>
          </div>
        )}
      </form>
    </div>
  ) : (
    <SlideOver large={false} id={UPLOAD_SLIDEOVER_COMPONENT_ID}>
      <div className="flex flex-col px-6 pt-8 pb-3 bg-white overflow-y-auto max-h-screen min-h-full">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full justify-between">
          <div>
            <h3
              className="font-medium text-lg pb-6"
              style={{ paddingBottom: infoVisible ? "0.5rem" : "1.5rem" }}
            >
              {t("general.upload")}
            </h3>
            {infoVisible && (
              <InformationFrame
                className="w-[90%] transition-opacity z-[1000]"
                innerClassName="flex p-4 my-1 rounded-lg relative"
                style={infoStyle}
                withTimeout={false}
                showCloseButton={false}
              >
                <div>{infoMessage}</div>
              </InformationFrame>
            )}
            <div className="space-y-3 w-full">
              {selectedFile && selectedFileMetadata && !!preview && (
                <div className="flex justify-center items-center flex-col">
                  <p className="text-gray-500 font-medium mb-2">
                    {t("pages.gallery.uploadPanel.selectedFile")}: {(selectedFile as File).name}
                  </p>
                  <MediaPreview
                    url={preview}
                    type={selectedFileMetadata.sourceMediaType}
                    className="max-h-[35vh]"
                  />
                </div>
              )}
              {!mediaAlreadyExists ? (
                <>
                  <div className="block text-lg font-medium text-gray-700 ">
                    {t("general.renameFile")}
                  </div>
                  <MediaNameInput
                    value={mediaNameToEdit}
                    onChange={setMediaNameToEdit}
                    maxLength={55}
                  />
                  {!nameUnique && (
                    <div className="text-red-600">{t("general.mediaNameAlreadyUsed")}</div>
                  )}
                  {selectedFileMetadata?.needAskMedia360Tags && (
                    <Media360TagDropdown
                      mode="upload"
                      mediaType={String(selectedFileMetadata?.sourceMediaType)}
                      onChangeCb={(v: any) => {
                        setMedia360Tag(v);
                      }}
                    />
                  )}
                  {selectedFileMetadata?.sourceMediaType === "model3d" && (
                    <div className="flex">
                      <Dropdown
                        options={media3DTypeOptions}
                        label={t("pages.gallery.filters.mediatype")}
                        onChangeCb={(e: any) => {
                          setMedia3DType(e);
                        }}
                        asDefaultValue
                        defaultValueIndex={0}
                      />
                      <div className="flex justify-end items-end ml-3 mb-2">
                        <Tooltip
                          message={t("pages.gallery.uploadPanel.tooltipfor3dTypes")}
                          forceSingleLine={false}
                          lineLength={"w-48"}
                        >
                          <InformationCircleIcon className="stroke-green-500 h-8 w-8 m-auto" />
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p>{t("pages.gallery.uploadPanel.fileAlreadyInGallery")}</p>
              )}
            </div>
          </div>
          <div className="flex h-full">
            {isMd5Loaded ? (
              <div className="self-end mb-4">
                <button
                  className="btn-alternative-outline mr-3 mt-2"
                  onClick={(e) => {
                    e.preventDefault();
                    setInfoVisible(false);
                    closeSliderAnimation();
                  }}
                >
                  {t("general.cancel")}
                </button>
                {infoVisible ? (
                  <></>
                ) : (
                  <button
                    className="btn-primary-fill mr-3 mt-2"
                    disabled={
                      !canUpload || (selectedFileMetadata?.needAskMedia360Tags && !media360Tag)
                    }
                  >
                    {`${
                      selectedFileMetadata?.sourceMediaType === "model3d"
                        ? media3DType === "media360"
                          ? t("pages.gallery.uploadPanel.askForPublication")
                          : t("pages.project.importMedia")
                        : t("pages.project.importMedia")
                    }`}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col mt-4 self-center">
                <Spinner relative />
                <p className="text-center italic">
                  {t("pages.gallery.uploadPanel.preparingMedia")}
                </p>
              </div>
            )}
          </div>
        </form>
      </div>
    </SlideOver>
  );
};

interface MediaPreviewParams {
  url: string;
  type: "video" | "image" | "audio" | "pdf" | "model3d";
  className?: string;
  mobile?: boolean;
}

const MediaPreview = ({ url, type, mobile = false, className = "" }: MediaPreviewParams) => {
  const style = mobile ? { maxHeight: "28vh", maxWidth: "90%" } : {};
  if (type === "video") {
    if (!url) return null;
    return <video controls src={url} style={style} className={className} />;
  }
  if (type === "image") {
    if (!url) return null;
    return <img src={url} style={mobile ? style : { minWidth: "20%" }} className={className} />;
  }
  if (type === "audio") {
    if (!url) return null;
    return <audio controls src={url} style={style} className={className} />;
  }
  if (type === "pdf") {
    return (
      <img
        src="https://s3.eu-west-3.amazonaws.com/storage.wixar.io/pdf-placeholder.jpg"
        style={style}
        className={className}
      />
    );
  }
  if (type === "model3d") {
    return (
      <img
        src="https://s3.eu-west-3.amazonaws.com/storage.wixar.io/3d-placeholder.png"
        style={style}
        className={className}
      />
    );
  }
  return null;
};
